import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Palette, Trash2, UserPlus, CopyPlus, ArrowUpDown, ChevronDown, Check, Globe, GlobeLock, Settings, Download, AtSign, Search, X, Tag, Heart, Layers } from 'lucide-react';
import { decodePaletteFromShare } from '../utils/paletteCode';
import UsernameModal from '../components/UsernameModal';
import ImportPaletteModal from '../components/ImportPaletteModal';
import ApplyPublisherModal from '../components/ApplyPublisherModal';
import MoodPicker, { MoodChips, MOOD_COLORS, SELECTED_COLORS } from '../components/MoodPicker';
import { MOODS, parseMoods, serializeMoods } from '../utils/moods';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { getUserPalettes, deletePalette as deletePaletteFirebase, duplicatePalette, getPaletteById, updatePaletteMoods } from '../config/savedPalettes';
import PaletteDetailModal from '../components/PaletteDetailModal';
import PageHelpModal from '../components/PageHelpModal';
import { useConfirmModal } from '../components/ConfirmModal';
import {
  isTrustedPublisher,
  getPublicPaletteCount,
  publishPalette,
  unpublishPalette,
  hasAcceptedGalleryToS,
  acceptGalleryToS,
  getPublishAnonymously,
  setPublishAnonymously,
  fetchPopularPalettes,
  fetchRecentPalettes,
} from '../services/galleryService';
import { GALLERY_CONFIG } from '../config/galleryConfig';

export default function MyPalettesPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [usernameModalResolve, setUsernameModalResolve] = useState(null);
  const navigate = useNavigate();
  const { showConfirm, ConfirmModalComponent } = useConfirmModal();
  const [palettes, setPalettes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPalette, setSelectedPalette] = useState(null);
  const [tosModal, setTosModal] = useState(null); // { resolve, publishAnonymously }
  const [showPublisherSettings, setShowPublisherSettings] = useState(false);
  const [publishAnonymously, setPublishAnonymouslyState] = useState(false);
  const [sortBy, setSortBy] = useState('date-new'); // 'date-new', 'date-old', 'name-az', 'name-za'
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMoodFilters, setActiveMoodFilters] = useState([]);
  const [moodFilterOpen, setMoodFilterOpen] = useState(false);
  const [showPublicOnly, setShowPublicOnly] = useState(false);
  const sortDropdownRef = useRef(null);
  const [isTrusted, setIsTrusted] = useState(false);
  const [togglingPaletteId, setTogglingPaletteId] = useState(null);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [publicHeartCounts, setPublicHeartCounts] = useState({}); // publicId -> heartCount

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sort + filter palettes
  const sortedPalettes = useMemo(() => {
    let result = [...palettes];

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q));
    }

    // Mood filter (match any selected mood)
    if (activeMoodFilters.length > 0) {
      result = result.filter(p => {
        const paletteMoods = parseMoods(p.moods);
        return activeMoodFilters.some(m => paletteMoods.includes(m));
      });
    }

    // Public only
    if (showPublicOnly) {
      result = result.filter(p => p.isPublic);
    }

    switch (sortBy) {
      case 'date-new': return result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'date-old': return result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'name-az':  return result.sort((a, b) => a.name.localeCompare(b.name));
      case 'name-za':  return result.sort((a, b) => b.name.localeCompare(a.name));
      default:         return result;
    }
  }, [palettes, sortBy, searchQuery, activeMoodFilters, showPublicOnly]);

  const handleMoodsChange = (paletteId, moodNames) => {
    const moodsString = serializeMoods(moodNames);
    // Optimistic update immediately
    setPalettes(prev => prev.map(p =>
      p.id === paletteId ? { ...p, moods: moodsString || undefined } : p
    ));
    // Debounced Firebase write — timer lives in PaletteCard via onMoodsChange
  };

  useEffect(() => {
    if (user) {
      loadPalettes();
      isTrustedPublisher(user.uid).then(setIsTrusted);
      getPublishAnonymously(user.uid).then(setPublishAnonymouslyState);
    }
  }, [user]);

  useEffect(() => {
    if (!isTrusted) return;
    Promise.all([fetchPopularPalettes(), fetchRecentPalettes()]).then(([pop, rec]) => {
      const map = {};
      [...(pop.palettes || []), ...(rec.palettes || [])].forEach(p => {
        if (p.id && !(p.id in map)) map[p.id] = p.heartCount || 0;
      });
      setPublicHeartCounts(map);
    });
  }, [isTrusted]);

  const loadPalettes = async () => {
    setIsLoading(true);
    try {
      const result = await getUserPalettes(user.uid);
      if (result.success) {
        setPalettes(result.palettes);
      } else {
        console.error('Failed to load palettes:', result.error);
      }
    } catch (error) {
      console.error('Error loading palettes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePalette = async (paletteId, paletteName) => {
    const confirmed = await showConfirm({
      title: t('savedPalettes.alerts.deleteTitle'),
      message: t('savedPalettes.alerts.confirmDelete', { name: paletteName }),
      confirmText: t('savedPalettes.buttons.delete'),
      cancelText: t('savedPalettes.buttons.cancel'),
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const result = await deletePaletteFirebase(user.uid, paletteId);
      if (result.success) {
        setPalettes(palettes.filter(p => p.id !== paletteId));
      } else {
        await showConfirm({
          title: t('savedPalettes.alerts.error'),
          message: t('savedPalettes.alerts.deleteFailed') + ': ' + result.error,
          confirmText: 'OK',
          variant: 'warning',
          showCancel: false
        });
      }
    } catch (error) {
      await showConfirm({
        title: t('savedPalettes.alerts.error'),
        message: t('savedPalettes.alerts.deleteFailed') + ': ' + error.message,
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false
      });
    }
  };

  const handleDuplicatePalette = async (paletteId) => {
    try {
      const result = await duplicatePalette(user.uid, paletteId);
      if (result.success) {
        await loadPalettes(); // Reload to show the new palette
      } else {
        await showConfirm({
          title: t('savedPalettes.alerts.error'),
          message: t('savedPalettes.alerts.duplicateFailed') + ': ' + result.error,
          confirmText: 'OK',
          variant: 'warning',
          showCancel: false
        });
      }
    } catch (error) {
      await showConfirm({
        title: t('savedPalettes.alerts.error'),
        message: t('savedPalettes.alerts.duplicateFailed') + ': ' + error.message,
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false
      });
    }
  };

  const handleTogglePublic = async (palette) => {
    if (togglingPaletteId) return;
    setTogglingPaletteId(palette.id);

    try {
      if (palette.isPublic) {
        const result = await unpublishPalette(user.uid, palette.id);
        if (result.success) {
          setPalettes(palettes.map(p =>
            p.id === palette.id ? { ...p, isPublic: false } : p
          ));
        } else {
          await showConfirm({
            title: 'Error',
            message: 'Failed to unpublish palette: ' + result.error,
            confirmText: 'OK',
            variant: 'warning',
            showCancel: false
          });
        }
      } else {
        // Require username or anonymous mode before publishing
        const existingAnon = await getPublishAnonymously(user.uid);
        if (!user?.username && !existingAnon) {
          const usernameSet = await new Promise((resolve) => {
            setShowUsernameModal(true);
            setUsernameModalResolve(() => resolve);
          });
          if (!usernameSet) {
            await showConfirm({
              title: 'Username required',
              message: 'You need a username to publish palettes. Set one above, or enable anonymous publishing in your publisher settings.',
              confirmText: 'OK',
              variant: 'warning',
              showCancel: false
            });
            setTogglingPaletteId(null);
            return;
          }
        }

        // Check ToS acceptance before first publish
        const tosAccepted = await hasAcceptedGalleryToS(user.uid);
        if (!tosAccepted) {
          // Show custom ToS modal with anonymous toggle
          const result = await new Promise((resolve) => {
            setTosModal({ resolve, publishAnonymously: existingAnon });
          });
          setTosModal(null);
          if (!result) {
            setTogglingPaletteId(null);
            return;
          }
          await acceptGalleryToS(user.uid, result.publishAnonymously);
        }

        const result = await publishPalette(user.uid, palette.id, palettes);
        if (result.success) {
          setPalettes(palettes.map(p =>
            p.id === palette.id ? { ...p, isPublic: true } : p
          ));
        } else {
          await showConfirm({
            title: 'Error',
            message: result.error,
            confirmText: 'OK',
            variant: 'warning',
            showCancel: false
          });
        }
      }
    } catch (error) {
      await showConfirm({
        title: 'Error',
        message: error.message,
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false
      });
    } finally {
      setTogglingPaletteId(null);
    }
  };

  const handleFindShadesForPalette = async (palette) => {
    try {
      let paletteMarkers = null;
      if (palette.code) {
        try {
          paletteMarkers = decodePaletteFromShare(palette.code);
        } catch (e) {
          // Fall back to fetching by ID below
        }
      }
      if (!paletteMarkers) {
        const result = await getPaletteById(user.uid, palette.id);
        if (!result.success) {
          await showConfirm({
            title: t('savedPalettes.alerts.error'),
            message: t('savedPalettes.alerts.loadFailed') + ': ' + result.error,
            confirmText: 'OK',
            variant: 'warning',
            showCancel: false,
          });
          return;
        }
        paletteMarkers = result.markers;
      }
      navigate('/shades', {
        state: {
          palette: paletteMarkers,
          paletteName: palette.name,
          paletteCode: palette.code,
        },
      });
    } catch (error) {
      await showConfirm({
        title: t('savedPalettes.alerts.error'),
        message: error.message,
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false,
      });
    }
  };

  const handleViewPalette = async (paletteId) => {
    try {
      const result = await getPaletteById(user.uid, paletteId);
      if (result.success) {
        setSelectedPalette({ id: paletteId, ...result.palette, markers: result.markers });
      } else {
        await showConfirm({
          title: t('savedPalettes.alerts.error'),
          message: t('savedPalettes.alerts.loadFailed') + ': ' + result.error,
          confirmText: 'OK',
          variant: 'warning',
          showCancel: false
        });
      }
    } catch (error) {
      await showConfirm({
        title: t('savedPalettes.alerts.error'),
        message: t('savedPalettes.alerts.loadFailed') + ': ' + error.message,
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false
      });
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <Palette size={64} className="mx-auto text-slate-400 mb-4" />
          <h1 className="text-3xl font-bold text-slate-800 mb-4">
            {t('savedPalettes.page.signInRequired')}
          </h1>
          <p className="text-slate-600 mb-6">
            {t('savedPalettes.page.signInMessage')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn btn-primary-green"
          >
            {t('savedPalettes.buttons.goHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto sm:px-6 lg:px-8 space-y-8">
      {/* Header */}

      <div className="text-center">
        <h2 className="text-3xl md:text-4xl font-display font-bold heading-display mb-3 animate-pulse bg-gradient-to-r from-emerald-400 to-teal-500">
          {t('savedPalettes.page.title')} <Palette className="inline-block w-8 h-8 pb-1 text-teal-500" />
        </h2>
        <p className="text-base md:text-md text-elegant max-w-2xl mx-auto">
          {t('savedPalettes.page.subtitle')}
        </p>
        <div className="inline-flex flex-col items-center gap-1">
          <PageHelpModal
            title={t('savedPalettes.help.title')}
            tips={[
              { text: t('savedPalettes.help.tip1') },
              { text: t('savedPalettes.help.tip2') },
              { text: t('savedPalettes.help.tip3') },
              { text: t('savedPalettes.help.tip4') },
              { text: "Get in touch with us if you want to publish your palettes to the public gallery"}
            ]}
            trigger={
              <p className="text-sm justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer animate-pulse mt-2 mx-auto inline-flex items-center gap-2">
                {t('common.needHelp')}
              </p>
            }
          />
          {!isTrusted && (
            <button
              onClick={() => setApplyModalOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-purple-500 transition-colors"
            >
              <UserPlus size={13} />
              Become a Publisher
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="text-slate-600 mt-4">{t('savedPalettes.page.loading')}</p>
        </div>
      )}

      {/* Trusted publisher publish count + settings */}
      {!isLoading && isTrusted && palettes.length > 0 && (
        <div className="flex justify-center items-center gap-2">
          <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
            <Globe size={13} className="inline-block mr-1 mb-0.5 text-emerald-500" />
            {getPublicPaletteCount(palettes)} / {GALLERY_CONFIG.MAX_PUBLIC_PALETTES_PER_USER} shared publicly
          </span>
          <button
            onClick={() => setShowPublisherSettings(true)}
            className="p-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
            title="Publisher settings"
          >
            <Settings size={14} />
          </button>
        </div>
      )}

      {/* Search + Mood Filters */}
      {!isLoading && palettes.length > 0 && (
        <div className="space-y-2">
          {/* Search bar + mood toggle */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search palettes…"
                className="w-full pl-9 pr-9 py-2 text-xs border border-slate-200 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              onClick={() => setMoodFilterOpen(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 ${
                moodFilterOpen || activeMoodFilters.length > 0
                  ? 'bg-teal-50 border-teal-300 text-teal-700'
                  : 'bg-white/80 backdrop-blur-sm border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              title="Filter by mood"
            >
              <Tag size={14} />
              {activeMoodFilters.length > 0 && (
                <span className="text-xs font-semibold">{activeMoodFilters.length}</span>
              )}
            </button>
            {isTrusted && (
              <button
                onClick={() => setShowPublicOnly(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 ${
                  showPublicOnly
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white/80 backdrop-blur-sm border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
                title="Show public only"
              >
                <Globe size={14} />
              </button>
            )}
          </div>

          {/* Mood filter chips — collapsible */}
          {moodFilterOpen && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2 animate-fadeIn">
              <div className="flex flex-wrap gap-1.5">
                {MOODS.map(mood => {
                  const active = activeMoodFilters.includes(mood);
                  return (
                    <button
                      key={mood}
                      onClick={() => setActiveMoodFilters(prev =>
                        active ? prev.filter(m => m !== mood) : [...prev, mood]
                      )}
                      className={`text-xs font-medium px-2 py-0.5 rounded-full border transition-all ${
                        active
                          ? (SELECTED_COLORS[mood] || 'bg-slate-700 text-white border-slate-700')
                          : (MOOD_COLORS[mood] || 'bg-slate-100 text-slate-500 border-slate-200') + ' opacity-50 hover:opacity-80'
                      }`}
                    >
                      {mood}
                    </button>
                  );
                })}
              </div>
              {activeMoodFilters.length > 0 && (
                <button
                  onClick={() => setActiveMoodFilters([])}
                  className="text-xs text-slate-400 hover:text-slate-600 underline"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sort Controls */}
      {!isLoading && palettes.length > 1 && (
        <div className="flex justify-end items-center gap-2">
          <button
            onClick={() => navigate('/shades')}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-fuchsia-200 rounded-xl bg-gradient-to-r from-fuchsia-50 to-violet-50 text-fuchsia-700 hover:from-fuchsia-100 hover:to-violet-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
            title="Find shades for any marker or palette"
          >
            <Layers size={13} className="text-fuchsia-500" />
            Shades
          </button>
          <button
            onClick={() => setImportModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-slate-200 rounded-xl bg-white/80 backdrop-blur-sm text-slate-600 hover:bg-slate-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
          >
            <Download size={13} className="text-slate-500" />
            Import
          </button>
          <div className="relative" ref={sortDropdownRef}>
            <button
              onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-slate-200 rounded-xl bg-white/80 backdrop-blur-sm text-slate-600 hover:bg-slate-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
            >
              <ArrowUpDown size={13} className="text-slate-500" />
              <span>{t(`savedPalettes.sort.${sortBy === 'date-new' ? 'dateNew' : sortBy === 'date-old' ? 'dateOld' : sortBy === 'name-az' ? 'nameAZ' : 'nameZA'}`)}</span>
              <ChevronDown size={13} className={`text-slate-400 transition-transform duration-200 ${sortDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {sortDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-xl shadow-soft-xl border border-slate-100 py-2 z-50 animate-fadeIn">
                {[
                  { value: 'date-new', label: t('savedPalettes.sort.dateNew') },
                  { value: 'date-old', label: t('savedPalettes.sort.dateOld') },
                  { value: 'name-az', label: t('savedPalettes.sort.nameAZ') },
                  { value: 'name-za', label: t('savedPalettes.sort.nameZA') }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSortBy(option.value);
                      setSortDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-2.5 text-left text-sm flex items-center justify-between hover:bg-slate-50 transition-colors ${
                      sortBy === option.value ? 'text-emerald-600 font-medium' : 'text-slate-700'
                    }`}
                  >
                    {option.label}
                    {sortBy === option.value && <Check size={16} className="text-emerald-500" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && palettes.length === 0 && (
        <div className="text-center py-12 rounded-2xl">
          <Palette size={64} className="mx-auto text-slate-400 mb-4" />
          <h2 className="text-2xl font-bold text-slate-700 mb-2">
            {t('savedPalettes.page.emptyTitle')}
          </h2>
          <p className="text-slate-600 mb-6">
            {t('savedPalettes.page.emptyMessage')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn btn-primary-green"
          >
            {t('savedPalettes.buttons.createPalette')}
          </button>
        </div>
      )}

      {/* Palettes Grid */}
      {!isLoading && sortedPalettes.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 items-start">
          {sortedPalettes.map((palette) => (
            <PaletteCard
              key={palette.id}
              palette={palette}
              onView={() => handleViewPalette(palette.id)}
              onDelete={() => handleDeletePalette(palette.id, palette.name)}
              onDuplicate={() => handleDuplicatePalette(palette.id)}
              onFindShades={() => handleFindShadesForPalette(palette)}
              onTogglePublic={isTrusted ? () => handleTogglePublic(palette) : null}
              isToggling={togglingPaletteId === palette.id}
              isTrusted={isTrusted}
              heartCount={palette.publicId ? publicHeartCounts[palette.publicId] : undefined}
              onMoodsChange={(moodNames) => handleMoodsChange(palette.id, moodNames)}
              userId={user.uid}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Palette Count */}
      {!isLoading && palettes.length > 0 && (
        <div className="mt-8 text-center text-slate-600">
          {t('savedPalettes.page.paletteCount', { count: palettes.length, max: 40 })}
        </div>
      )}

      {/* Gallery status legend — only shown for trusted publishers */}
      {!isLoading && isTrusted && palettes.length > 0 && (
        <div className="flex flex-wrap justify-center gap-4 mt-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <GlobeLock size={13} className="text-slate-400" />
            Private
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Globe size={13} className="text-amber-400" />
            Pending sync
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Globe size={13} className="text-emerald-500" />
            Live in gallery
          </div>
        </div>
      )}

      {/* Help Notice */}
        <div className="mt-12 text-center">
          <p className="text-sm text-slate-500">
            {t('savedPalettes.page.help.prefix')}{' '}
            <a
              href="mailto:markersync@gmail.com"
              className="text-blue-500 hover:text-blue-600 transition-colors"
            >
              markersync@gmail.com
            </a>
            {t('savedPalettes.page.help.suffix')}
          </p>
        </div>

      {/* Detail Modal */}
      {selectedPalette && (
        <PaletteDetailModal
          isOpen={true}
          paletteId={selectedPalette.id}
          preloadedPalette={selectedPalette}
          preloadedMarkers={selectedPalette.markers}
          onClose={() => setSelectedPalette(null)}
          onDelete={async () => {
            await handleDeletePalette(selectedPalette.id, selectedPalette.name);
            setSelectedPalette(null);
          }}
          onDuplicate={async () => {
            await handleDuplicatePalette(selectedPalette.id);
            setSelectedPalette(null);
          }}
          isTrusted={isTrusted}
          onNameUpdate={(newName) => {
            setPalettes(palettes.map(p =>
              p.id === selectedPalette.id ? { ...p, name: newName } : p
            ));
            setSelectedPalette({ ...selectedPalette, name: newName });
          }}
          onPublicNameUpdate={(newPublicName) => {
            setPalettes(palettes.map(p =>
              p.id === selectedPalette.id ? { ...p, publicName: newPublicName || undefined } : p
            ));
            setSelectedPalette({ ...selectedPalette, publicName: newPublicName || undefined });
          }}
          onMoodsUpdate={(moodsString) => {
            setPalettes(palettes.map(p =>
              p.id === selectedPalette.id ? { ...p, moods: moodsString || undefined } : p
            ));
            setSelectedPalette(prev => ({ ...prev, moods: moodsString || undefined }));
          }}
          onTogglePublic={isTrusted ? async () => {
            await handleTogglePublic(selectedPalette);
            setSelectedPalette(prev => ({ ...prev, isPublic: !prev.isPublic }));
          } : null}
          isToggling={togglingPaletteId === selectedPalette.id}
          heartCount={selectedPalette.publicId ? publicHeartCounts[selectedPalette.publicId] : undefined}
        />
      )}

      {/* Import Modal */}
      <ImportPaletteModal
        isOpen={importModalOpen}
        onClose={() => { setImportModalOpen(false); loadPalettes(); }}
      />

      {/* Confirm Modal */}
      {ConfirmModalComponent}

      {/* Publisher settings modal */}
      {showPublisherSettings && (
        <PublisherSettingsModal
          publishAnonymously={publishAnonymously}
          onSave={async (newAnon) => {
            await setPublishAnonymously(user.uid, newAnon);
            setPublishAnonymouslyState(newAnon);
            setShowPublisherSettings(false);
          }}
          onClose={() => setShowPublisherSettings(false)}
          onOpenUsernameModal={() => { setShowPublisherSettings(false); setShowUsernameModal(true); }}
          username={user?.username}
        />
      )}

      {/* Gallery ToS Modal */}
      {tosModal && (
        <GalleryToSModal
          publishAnonymously={tosModal.publishAnonymously}
          onConfirm={(publishAnonymously) => tosModal.resolve({ publishAnonymously })}
          onCancel={() => tosModal.resolve(null)}
          onOpenUsernameModal={() => setShowUsernameModal(true)}
          username={user?.username}
        />
      )}

      {/* Username Modal */}
      <UsernameModal isOpen={showUsernameModal} onClose={() => {
        setShowUsernameModal(false);
        if (usernameModalResolve) {
          usernameModalResolve(!!user?.username);
          setUsernameModalResolve(null);
        }
      }} />

      {/* Apply to Become a Publisher Modal */}
      <ApplyPublisherModal isOpen={applyModalOpen} onClose={() => setApplyModalOpen(false)} />
    </div>
  );
}

// Palette Card Component
function PaletteCard({ palette, onView, onDelete, onDuplicate, onFindShades, onTogglePublic, isToggling, isTrusted, heartCount, onMoodsChange, userId, t }) {
  const [showMoodPicker, setShowMoodPicker] = useState(false);
  const moodNames = parseMoods(palette.moods);
  const moodSaveTimer = useRef(null);
  const cardRef = useRef(null);

  const handleMoodsChange = (newMoodNames) => {
    onMoodsChange(newMoodNames); // optimistic update in parent
    clearTimeout(moodSaveTimer.current);
    moodSaveTimer.current = setTimeout(() => {
      updatePaletteMoods(userId, palette.id, serializeMoods(newMoodNames) || null);
    }, 800);
  };

  useEffect(() => {
    if (!showMoodPicker) return;
    const handleClickOutside = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setShowMoodPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoodPicker]);

  return (
    <div ref={cardRef} className="group w-full bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all duration-200 overflow-hidden">
      {/* Color strip — clickable to view */}
      <div
        className="flex h-20 w-full overflow-hidden relative cursor-pointer"
        onClick={onView}
      >
        {palette.previewColors.map((hex, index) => (
          <div
            key={index}
            className="flex-1 transition-transform duration-200 group-hover:scale-y-105"
            style={{ backgroundColor: hex }}
          />
        ))}
        {palette.previewColors.length === 0 && (
          <div className="flex-1 bg-slate-100" />
        )}
        {/* Public / Pending badge overlay */}
        {isTrusted && palette.isPublic && (
          palette.publicId
            ? <div className="absolute top-2 left-2 flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                <Globe size={10} />
                Public
              </div>
            : <div className="absolute top-2 left-2 flex items-center gap-1 bg-amber-400 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                <Globe size={10} />
                Pending
              </div>
        )}
      </div>

      {/* Name */}
      <div className="px-3 pt-2 pb-1 cursor-pointer" onClick={onView}>
        <p className="text-sm font-semibold text-slate-800 truncate leading-tight">
          {palette.name}
        </p>
      </div>

      {/* Mood chips (read-only display) */}
      {moodNames.length > 0 && !showMoodPicker && (
        <div className="px-3 pb-1">
          <MoodChips moodNames={moodNames} />
        </div>
      )}

      {/* Mood picker (expanded) */}
      {showMoodPicker && (
        <div className="px-3 pb-2" onClick={e => e.stopPropagation()}>
          <MoodPicker
            selected={moodNames}
            onChange={handleMoodsChange}
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="px-2 pb-2 flex items-center justify-end gap-0.5">
        {/* Mood toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); setShowMoodPicker(v => !v); }}
          className={`p-1.5 rounded-lg transition-colors ${showMoodPicker ? 'text-teal-500 bg-teal-50' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
          title="Edit moods"
        >
          <Tag size={15} />
        </button>

        {onFindShades && (
          <button
            onClick={(e) => { e.stopPropagation(); onFindShades(); }}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-fuchsia-50 hover:text-fuchsia-500 transition-colors"
            title="Find shades for this palette"
          >
            <Layers size={15} />
          </button>
        )}

        {onTogglePublic && (() => {
          const isPendingPublish = palette.isPublic && !palette.publicId;
          return (
            <button
              onClick={(e) => { e.stopPropagation(); onTogglePublic(); }}
              disabled={isToggling}
              className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 ${
                isPendingPublish
                  ? 'text-amber-400 hover:bg-amber-50'
                  : palette.isPublic
                    ? 'text-emerald-500 hover:bg-emerald-50'
                    : 'text-slate-400 hover:bg-slate-100'
              }`}
              title={isPendingPublish ? 'Pending sync…' : palette.isPublic ? 'Make private' : 'Make public'}
            >
              {palette.isPublic ? <Globe size={15} /> : <GlobeLock size={15} />}
            </button>
          );
        })()}
        <button
          onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          title={t('savedPalettes.buttons.duplicate')}
        >
          <CopyPlus size={15} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
          title={t('savedPalettes.buttons.delete')}
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

// Publisher settings modal — toggle anonymous publish preference
function PublisherSettingsModal({ publishAnonymously: initialAnon, onSave, onClose, onOpenUsernameModal, username }) {
  const [anon, setAnon] = useState(initialAnon);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    return () => { document.documentElement.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(anon);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] backdrop-blur-sm bg-black/30 flex items-center justify-center p-4 animate-fadeOnly"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 p-6 animate-slideUp">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-900">Publisher settings</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <Settings size={16} />
          </button>
        </div>

        <button
          onClick={onOpenUsernameModal}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-slate-100 transition-all mb-3"
        >
          <div className="text-left">
            <p className="text-sm font-medium text-slate-800">Username</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {username ? `Showing as @${username}` : 'Not set — a username is required to publish'}
            </p>
          </div>
          <AtSign size={16} className="text-slate-400 flex-shrink-0" />
        </button>

        <button
          onClick={() => setAnon(!anon)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-slate-100 transition-all mb-5"
        >
          <div className="text-left">
            <p className="text-sm font-medium text-slate-800">Publish anonymously</p>
            <p className="text-xs text-slate-500 mt-0.5">Your name will appear as "MarkerSync User"</p>
          </div>
          <div className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 flex items-center px-1 ${anon ? 'bg-blue-500' : 'bg-slate-300'}`}>
            <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${anon ? 'translate-x-4' : 'translate-x-0'}`} />
          </div>
        </button>

        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mb-4">
          <Globe size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700 leading-relaxed">
            Changes apply to all your public palettes and take effect on the next gallery sync, which may take a few hours.
          </p>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-slate-800 hover:bg-slate-900 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Gallery ToS Modal with anonymous toggle
function GalleryToSModal({ publishAnonymously: initialAnon, onConfirm, onCancel, onOpenUsernameModal, username }) {
  const [anon, setAnon] = useState(initialAnon);

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    return () => { document.documentElement.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onCancel]);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] backdrop-blur-sm bg-black/30 flex items-center justify-center p-4 animate-fadeOnly"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-200 p-6 animate-slideUp">
        <div className="flex items-start gap-3 mb-4">
          <div className="text-blue-500 mt-0.5 flex-shrink-0">
            <Globe size={22} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Gallery Terms of Service</h3>
            <p className="text-sm text-slate-600 leading-relaxed">Before publishing to the gallery, please note:</p>
          </div>
        </div>

        <ul className="text-sm text-slate-600 space-y-1.5 mb-4 ml-9">
          <li>• Your display name on public palettes will be your <strong>username</strong>. You can also publish fully anonymously.</li>
          <li>• Palette titles must follow community guidelines.</li>
          <li>• Abuse will result in removal from the publisher whitelist.</li>
        </ul>

        {!username && (
          <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-xl px-3 py-2.5 mb-4 ml-9">
            <p className="text-xs text-purple-700">No username set — required to publish unless anonymous.</p>
            <button
              onClick={onOpenUsernameModal}
              className="ml-3 shrink-0 flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900 underline underline-offset-2"
            >
              <AtSign size={12} /> Set one
            </button>
          </div>
        )}
        {username && (
          <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-xl px-3 py-2.5 mb-4 ml-9">
            <p className="text-xs text-purple-700">Showing as <strong>@{username}</strong></p>
            <button
              onClick={onOpenUsernameModal}
              className="ml-3 shrink-0 flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900 underline underline-offset-2"
            >
              <AtSign size={12} /> Change
            </button>
          </div>
        )}

        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mb-5 ml-9">
          <Globe size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700 leading-relaxed">
            Palettes appear in the gallery after the next sync, which may take a few hours. Your palette will show as <strong>Pending</strong> until then.
          </p>
        </div>

        {/* Anonymous toggle */}
        <button
          onClick={() => setAnon(!anon)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-slate-100 transition-all mb-5"
        >
          <div className="text-left">
            <p className="text-sm font-medium text-slate-800">Publish anonymously</p>
            <p className="text-xs text-slate-500 mt-0.5">Your name will appear as "MarkerSync User"</p>
          </div>
          <div className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 flex items-center px-1 ${anon ? 'bg-blue-500' : 'bg-slate-300'}`}>
            <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${anon ? 'translate-x-4' : 'translate-x-0'}`} />
          </div>
        </button>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(anon)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Agree & Publish
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
